import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { API, WeeklyScheduleDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from '../../../services/error.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit' | 'location' | 'delete';

@Component({
  selector: 'sb-page-weekly-schedule-list',
  templateUrl: './weekly-schedule-list.component.html',
  styleUrls: ['./weekly-schedule-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeeklyScheduleListComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  public readonly api = inject(API);
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly translate = inject(TranslateService);
  private readonly errorHandler = inject(ErrorService);
  private readonly modalService = inject(ModalService);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  isInModalContext: boolean;

  // table wrapper variables
  weeklySchedules: WeeklyScheduleDTO[] = [];
  selectedSchedule: WeeklyScheduleDTO = null;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit() {
    if (this.isInModalContext) {
      this.selectedSchedule = this.modalNavigation.getCurrentNavigation().data.schedule;
    }
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ca_md_WeeklySchedule_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (weeklySchedules: WeeklyScheduleDTO[]) => {
          this.weeklySchedules = this.buildSplitButtons(weeklySchedules);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  deleteWeeklySchedule(weeklySchedule: WeeklyScheduleDTO) {
    if (!weeklySchedule || weeklySchedule.id == null || weeklySchedule.id <= -1) {
      return;
    }
    this.modalService
      .confirm({
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
        title: this.translate.instant('lang.globals.confirmation'),
        message: this.translate.instant('lang.weeklySchedule.confirmDelete'),
      })
      .then((res: boolean) => {
        if (!res) {
          return;
        }
        this.api
          .ca_md_WeeklySchedule_deleteById(weeklySchedule.id)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.refreshFromServer();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      })
      .catch(() => {});
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.weekly_schedule_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.weeklyScheduleEdit,
          queryParams: { returnTo: this.util.getPageLink() },
          pathParams: [{ id: 'weeklyScheduleID', value: item?.id }],
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.location_list])) {
        item.sbButtons.push({
          icon: 'location',
          tooltip: 'lang.globals.locations',
          link: RoutingID.locations,
          queryParams: { returnTo: this.util.getPageLink(), schedules: item?.id },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.weekly_schedule_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(weeklySchedule: WeeklyScheduleDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.deleteWeeklySchedule(weeklySchedule);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  selectSchedule(schedule: WeeklyScheduleDTO) {
    if (this.isInModalContext) {
      this.modalNavigation.result(schedule);
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
